import React, { useState, useEffect } from "react";
import { Chart } from "react-google-charts";
import { renderNoData } from "../../common/Dashboard/utils";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { getService, getAxiosHeaders } from "../../../library/RestAPI";
import { domain_vizr } from "../../../redux/constants/constants";
import { sanitizeDOMString } from "../../../components/common/utilities";
import RenderBarChart from "./RenderBarChart";
import Grid from "@material-ui/core/Grid";

const colorLegendMap = {};
const staticColors = [
  "#1E90FF", // Dodger Blue
  "#00CED1", // Dark Turquoise
  "#FFD700", // Gold
  "#8A2BE2", // Blue Violet
  "#FF6347", // Tomato
  "#20B2AA", // Light Sea Green
  "#DC143C", // Crimson
  "#7FFF00", // Chartreuse
  "#FF4500", // Orange Red
  "#00BFFF", // Deep Sky Blue
  "#ADFF2F", // Green Yellow
  "#00FA9A", // Medium Spring Green
  "#FF8C00", // Dark Orange
  "#9932CC", // Dark Orchid
  "#40E0D0", // Turquoise
  "#B22222", // Fire Brick
  "#00FF7F", // Spring Green
  "#4682B4", // Steel Blue
  "#FF00FF", // Magenta
  "#32CD32", // Lime Green
  "#6495ED", // Cornflower Blue
  "#00FF00", // Lime
  "#FFA07A", // Light Salmon
  "#6A5ACD", // Slate Blue
  "#00FFFF", // Aqua
  "#FFDEAD", // Navajo White
  "#A0522D"  // Sienna
];
const getColorFromLabel = (label) => {
  switch (label) {
    case "Done":
      return "color: #47BC8A";
    case "In Progress":
      return "color: #FCDC76";
    case "To Do":
      return "color: #DE350B";
    case "Policy Development":
      return "color: #FF8B00";
    case "Incident Response":
      return "color: #BF2600";
    case "Planning":
      return "color: #006644";
    case "Governance":
      return "color: #47BC8A";
    case "BCP/DR":
      return "color: #FEB981";
    case "Training":
      return "color: #403294";
    case "Onboarding":
      return "color: #8165C6";
    default:
      if (colorLegendMap[label]) {
        return colorLegendMap[label];
      } else {
        // Assign a new color from the staticColors array
        const colorIndex = Object.keys(colorLegendMap).length % staticColors.length;
        const newColor = staticColors[colorIndex];
        colorLegendMap[label] = `color: ${newColor}`;
        return colorLegendMap[label];
      }
  }
};

const RenderStatusChart = ({ statusDashboard, avoidRendering }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState(null);
  const [legendColors, setLegendColors] = useState({}); // State to store legend colors
  const [finalTotal, setFinalTotal] = useState(0);

  /*write a transformation function that takes input data as mockData variable in the format mentioned below and gives ouptut data in the format react-google-charts needs to render multi bar chart export const data as fallows */
  const transformDataForBarChart = (apiresponse, showPercent = false) => {
    const key = Object.keys(apiresponse)[0];
    const tableData = apiresponse[key];
    const sortedMonths = Object.keys(tableData).sort((a, b) => {
      const parseMonth = (str) => {
        const [month, year] = str.split(" ");
        return new Date(`${month} 1, ${year}`);
      };
      return parseMonth(a) - parseMonth(b);
    });
    const categories = new Set();
    Object.values(tableData).forEach((monthData) => {
      Object.keys(monthData).forEach((category) => { if (category !== "Total (Issues)") categories.add(category) });
    });

    // Calculate totals for each category
    const categoryTotals = {};
    Object.values(tableData).forEach((monthData) => {
      Array.from(categories).forEach((category) => {
        categoryTotals[category] = (categoryTotals[category] || 0) + (monthData[category] || 0);
      });
    });

    // Update headers to include totals
    const chartData = [[
      "Month",
      ...Array.from(categories).flatMap((category) => [
        `${category} | ${categoryTotals[category]}`,
        { role: "annotation" },
        { role: "style" }
      ])
    ]];
    const categoryColors = {}; // Store colors for each category
    let finalTotalCount = 0;
    sortedMonths.forEach((month) => {
      const row = [month];
      Array.from(categories).forEach((category) => {
        const total = tableData[month]["Total (Issues)"] || 0;
        const value = tableData[month][category] || 0;
        finalTotalCount += value;
        const percentage = total && value > 0 ? `${((value / total) * 100).toFixed(0)}%` : null;
        const val = value > 0 ? value.toString() : null;

        // Assign colors dynamically
        const style = getColorFromLabel(category);
        categoryColors[category] = style.match(/color: (#?[a-fA-F0-9]+)/)[1]; // Extract color code
        row.push(value, showPercent ? percentage : val, style);
      });
      chartData.push(row);
    });

    return { chartData, categoryColors, finalTotalCount };
  };

  // write a transformation function that takes input data as mockData variable in the format mentioned below and gives ouptut data in the format react-google-charts needs to render dounut chart chart export const data as fallows
  const transformDataForDonutChart = (apiresponse) => {
    const key = Object.keys(apiresponse)[0]; // Extract the main key (e.g., "Work Type Chart")
    // Calculate total for each work type
    const workTypeCounts = {};
    Object.values(apiresponse[key]).forEach((monthData) => {
      Object.entries(monthData).forEach(([workType, count]) => {
        workTypeCounts[workType] = (workTypeCounts[workType] || 0) + count;
      });
    });
    // Sort work types by total descending
    const sortedWorkTypes = Object.entries(workTypeCounts)
      .sort((a, b) => b[1] - a[1]);
    // Update headers to include totals for legend
    const chartData = [["Work Type", "Count"]];
    const colors = [];
    sortedWorkTypes.forEach(([workType, count]) => {
      chartData.push([`${workType} | ${count}`, count]); // Append total to label
      colors.push(getColorFromLabel(workType).match(/color: (#?[a-fA-F0-9]+)/)[1]);
    });
    return { chartData, colors };
  };


  const transformDataForTable = (apiresponse) => {
    if (!Array.isArray(apiresponse)) return [];
    const headers = statusDashboard.desiredOrder || Object.keys(apiresponse[0]);
    const rows = apiresponse.map((item) =>
      headers.map((header) => {
        const cellValue = item[header];
        return cellValue;
      })
    );
    return { headers: headers, rows: rows };
  };

  useEffect(() => {
    if (statusDashboard && statusDashboard.url) {
      setLoading(true);
      getService({
        method: "GET",
        url: domain_vizr + statusDashboard.url,
        headers: getAxiosHeaders(true),
        timeout: 1800000,
      }).then((res) => {
        if (res.status === 200) {
          let apiData = res.data;
          if (statusDashboard.transformFn) {
            apiData = statusDashboard.transformFn(res.data);
          }
          if (statusDashboard.type === "multibarchart") {
            const { chartData, categoryColors, finalTotalCount } = transformDataForBarChart(apiData, statusDashboard.showPercent);
            setData(apiData);
            setLegendColors(categoryColors); // Save legend colors
            setFinalTotal(finalTotalCount);
          } else if (statusDashboard.type === "table") {
            if (apiData && apiData.title) {
              const transformedData = transformDataForTable(apiData.issues);
              setTitle(apiData.title);
              setData(transformedData);
            } else {
              const transformedData = transformDataForTable(apiData);
              setData(transformedData);
            }
          } else if (statusDashboard.type === "piechart") {
            const { chartData, colors } = transformDataForDonutChart(apiData);
            setData(chartData);
            setLegendColors(colors); // Save legend colors for piechart
          } else {
            setData(res.data);
          }
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    }
  }, [statusDashboard]);

  const chartOptions = {
    piechart: {
      chartType: "PieChart",
      options: {
        pieHole: 0.4,
        legend: {
          position: "right",
          alignment: "start",
          textStyle: {
            color: "#000",
          },
        },
        colors: legendColors, // Use dynamically generated colors
        animation: {
          startup: true,
          easing: "out",
          duration: 1000, // Animation duration in milliseconds
        },
        chartArea: { width: "100%", height: "100%" },
      },
    },
    multibarchart: {
      chartType: "ColumnChart",
      options: {
        title: '',
        legend: {
          position: 'top',
          maxLines: 3,
          alignment: 'center',
          margin: 20, // Add margin to create more gap
          textStyle: {
            color: "#000",
          },
        },
        colors: Object.values(legendColors), // Use dynamically generated colors
        annotations: { style: 'line' },
        displayAnnotations: true,
        bar: { groupWidth: '75%' },
        annotations: {
          alwaysOutside: true,
          textStyle: {
            fontSize: 12,
            color: '#000',
          },
        },
        vAxis: {
          title: 'ISSUES',
          format: '0',
          minValue: 0,
          gridlines: { color: 'transparent' }, // Remove horizontal grid lines
        },
        hAxis: {
          title: 'DUE DATE',
        },
        animation: {
          startup: true,
          easing: 'out',
          duration: 1000, // Animation duration in milliseconds
        },
      },
    },
  };

  const renderList = () => {
    // if type is list show bullet points list
    const key = Object.keys(data);
    const value = data[key];
    if (value && value.renderedFields && value.renderedFields.description) {
      const sanitizedValue = sanitizeDOMString(value.renderedFields.description);
      // removed first p tag but now jira description doesnt have title hence we comment this code
      // const valueWithoutFirstP = sanitizedValue.replace(/<p>.*?<\/p>/i, "");
      // Remove <h4><a name="InProgress"></a>In Progress</h4> from the HTML string
      const valueWithoutInProgressHeader = sanitizedValue.replace(
        /<h4><a name="InProgress"><\/a>In Progress<\/h4>/i,
        ""
      );
      return (
        <>
          <div dangerouslySetInnerHTML={{ __html: valueWithoutInProgressHeader }} />
        </>
      );
    } else {
      avoidRendering(statusDashboard);
      return renderNoData("30%");
    }
  }

  const renderTable = () => {
    if (data && data.headers && data.rows) {
      const hdrClass = (statusDashboard.cellClass) ? statusDashboard.cellClass + " tbl-header" : "tbl-header";
      const cellClass = (statusDashboard.cellClass) ? statusDashboard.cellClass : "";
      return (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {data.headers.map((header, index) => (
                  <TableCell className={hdrClass} key={index}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <TableCell className={cellClass} key={cellIndex}>
                      {cell || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      avoidRendering(statusDashboard);
      return renderNoData("30%");
    }
  }

  const renderChart = (showTotal = false) => {
    const chartConfig = chartOptions[statusDashboard.type];
    if (!chartConfig) {
      avoidRendering(statusDashboard);
      return renderNoData("30%");
    }
    return (
      <Grid
        container
        className="ht-100"
        justify="flex-end"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={12}>

        <Chart
          width={"100%"}
          height={"100%"}
          chartType={chartConfig.chartType}
          loader={<div>Loading Chart...</div>}
          data={data}
          options={chartConfig.options}
        />
        {showTotal && <span className="show-total">Total: {finalTotal}</span>}
        </Grid>
      </Grid>
    );
  }

  const renderBarChart = (showTotal = false) => {
    const chartConfig = chartOptions[statusDashboard.type];
    if (!chartConfig) {
      avoidRendering(statusDashboard);
      return renderNoData("30%");
    }
    return (
      <div className="bar-chart-container">
        <RenderBarChart data={data} colorMap={legendColors} />
        {showTotal && <span className="show-total">Total: {finalTotal}</span>}
      </div>
    );
  }

  const renderHeader = () => {
    if (statusDashboard.type === "header") {
      return null;
    }
    return <h3 className="section-header">{title || statusDashboard.name}</h3>
  }

  const renderContent = () => {
    if (statusDashboard.type !== "header") {
      if (loading) {
        return <div>Loading...</div>;
      } else if (!data || !statusDashboard || !statusDashboard.type) {
        avoidRendering(statusDashboard);
        return renderNoData("30%");
      }
    }
    switch (statusDashboard.type) {
      case "list":
        return renderList();
      case "table":
        return renderTable();
      case "piechart":
        return renderChart();
      case "multibarchart":
        return renderBarChart(true);
      case "header":
        return <h2 className="parent-heading">{statusDashboard.name}</h2>
      default:
        return null;
    }
  }

  return <>
    {renderHeader()}
    <div className="mrg-top-10 ht-100">
      {renderContent()}
    </div>
  </>

}

export default RenderStatusChart;